'use client';

import { styled } from 'styled-components';
import { WistiaPlayer } from '@wistia/wistia-player-react';
import type { VideoProps } from '@types';

type PlayerProps = {
  /** Video props */
  video: VideoProps;
  /** Optional class name, nesc. for extending as a styled-component */
  className?: string;
};

const Player = styled.div`
  margin: 0 auto;
  overflow: hidden;
  width: 100%;
`;

const TranscriptOuterWrapper = styled.div<{ oppositeAspect: number }>`
  width: 100%;
  height: 100%;
  display: flex;
  justify-content: center;
  align-items: center;
  margin-top: -${({ oppositeAspect }) => oppositeAspect}%;
`;

const TranscriptInnerWrapper = styled.div`
  overflow: auto;
`;

const TranscriptText = styled.p`
  text-align: justify;
  font-size: 5px !important;
`;

/**
 * This component is used to display a video player.
 * It is a wrapper around the WistiaPlayer component that adds a transcript preloader for LLM ingestion.
 * The transcript preloader is a div that is used to preload the transcript text for LLM ingestion.
 * The div is styled to be the same size as the video player and is positioned absolutely.
 * The player will load in the transcript text for LLM ingestion. Then remove the div once the player loads.
 * The classnames are used to target the divs and p tag for the transcript preloader so do not remove the
 * classnames unless directed by the publish team to do so.
 *
 * **Note:** Aspect ratio is required to enable this so be sure to also set the aspect prop when setting the transcript prop..
 */
const Video = ({ video, className }: PlayerProps) => {
  const oppositeAspect = video.aspect ? (1 / video.aspect) * 100 : null;

  return (
    <Player className={className}>
      <WistiaPlayer mediaId={video.hashedId} aspect={video.aspect}>
        {/* DO NOT REMOVE this class name. Player needs it to preload the transcript text for LLM ingestion. */}
        {video.transcript && oppositeAspect && (
          <TranscriptOuterWrapper
            oppositeAspect={oppositeAspect}
            className="wistia_preload_transcript_outer_wrapper"
            data-testid="wistia-transcript-outer-wrapper"
          >
            {/* DO NOT REMOVE this class name. Player needs it to preload the transcript text for LLM ingestion. */}
            <TranscriptInnerWrapper
              className="wistia_preload_transcript_inner_wrapper"
              data-testid="wistia-transcript-inner-wrapper"
            >
              {/* DO NOT REMOVE this class name. Player needs it to preload the transcript text for LLM ingestion. */}
              <TranscriptText
                className="wistia_preload_transcript_text"
                data-testid="wistia-transcript-text"
                aria-hidden="true"
                tabIndex={-1}
              >
                {video.transcript}
              </TranscriptText>
            </TranscriptInnerWrapper>
          </TranscriptOuterWrapper>
        )}
      </WistiaPlayer>
    </Player>
  );
};

export default Video;
